// A conta que o cupom de fechamento da mesa imprime — uma só, para o HTML do
// app e para o texto do servidor (`conta_cupom`). Regras do dono (21/10/2026):
//  1. o cupom é reflexo do sistema: TOTAL = subtotal − descontos, nunca a soma
//     bruta dos itens;
//  2. pagamento já lançado fica atrelado à mesa e sai no cupom, com "Falta pagar";
//  3. mesa com 2+ pessoas sai agrupada: "-- Camila --", itens, "SUBTOTAL CAMILA".
//
// Dono do item (mesma regra de `backend/api/v1/mesa_qr/conta.py: dono_de`):
// `_cliente_id`; com `_dono_anterior` o item passou para a mesa (Atendimento);
// sem dono → Atendimento. Agrupa por id, nunca por nome.
import { lerPagamentos } from "./contaMesa";

const centavos = (v) => Math.round((Number(v) || 0) * 100);
const reais = (c) => Math.round(c) / 100;
const primeiroNome = (s) => String(s || "").trim().split(/\s+/)[0] || "";

const precoDe = (it) => Number(it.preco_unitario || it.final_price || it.sale_price || it.preco || it.price || 0) || 0;
const qtdDe = (it) => Number(it.quantidade || it.qty || it.quantity || 1) || 1;

function normalizar(it, pedido) {
  const n = {
    ...it,
    nome: it.nome || it.name || "Item",
    preco_unitario: precoDe(it),
    quantidade: qtdDe(it),
    deleted: it.deleted || false,
  };
  // Item da rodada do Mesa QR lido do pedido herda o cliente do pedido
  if (pedido && !n._cliente_id && !n._dono_anterior && pedido.cliente_id && (n._qid || /mesa\s*qr/i.test(String(pedido.modulo || "")))) {
    n._cliente_id = pedido.cliente_id;
    n._cliente_nome = n._cliente_nome || pedido.cliente_nome;
  }
  return n;
}

function juntarItens(mesa, pedidos) {
  if (Array.isArray(mesa?.items) && mesa.items.length > 0) return mesa.items.map((it) => normalizar(it, null));
  const out = [];
  for (const p of pedidos || []) for (const it of p.items || []) out.push(normalizar(it, p));
  return out;
}

function montarGrupos(itens, agrupar) {
  const vivos = itens.filter((i) => !i.deleted);
  const soma = (lista) => reais(lista.reduce((s, i) => s + centavos(i.preco_unitario * i.quantidade), 0));
  const todos = [{ chave: "todos", rotulo: "", itens: vivos, subtotal: soma(vivos) }];
  if (!agrupar) return { grupos: todos, agrupado: false };
  const porChave = new Map();
  for (const it of vivos) {
    const dono = it._dono_anterior ? null : it._cliente_id || null;
    const chave = dono ? `c:${dono}` : "atendimento";
    if (!porChave.has(chave)) {
      porChave.set(chave, { chave, rotulo: dono ? primeiroNome(it._cliente_nome || it._por) || "Cliente" : "Atendimento", itens: [] });
    }
    const g = porChave.get(chave);
    if (dono && g.rotulo === "Cliente") g.rotulo = primeiroNome(it._cliente_nome || it._por) || "Cliente";
    g.itens.push(it);
  }
  if (porChave.size < 2) return { grupos: todos, agrupado: false };
  const lista = [...porChave.values()].sort((a, b) => (a.chave === "atendimento") - (b.chave === "atendimento"));
  const vistos = {};
  for (const g of lista) {
    if (g.chave === "atendimento") { g.subtotal = soma(g.itens); continue; }
    vistos[g.rotulo] = (vistos[g.rotulo] || 0) + 1;
    if (vistos[g.rotulo] > 1) g.rotulo = `${g.rotulo} ${vistos[g.rotulo]}`;
    g.subtotal = soma(g.itens);
  }
  return { grupos: lista, agrupado: true };
}

// Uma parte de desconto vira { rotulo, valor }; partes de mesmo rótulo se somam
function rotuloDesconto(m) {
  if (m?.tipo === "coupon") return m.cupom && m.cupom !== "cupom" ? `Cupom (${String(m.cupom).toUpperCase()})` : "Cupom";
  if (m?.tipo === "percent" && Number(m.percentual) > 0) return `Desconto (${Number(m.percentual).toFixed(1).replace(".", ",")}%)`;
  return m?.tipo === "percent" ? "Desconto" : "Desconto manual";
}

// Pedido reaberto ainda em aberto: o desconto dele (inclusive o cashback, que a
// reabertura não zera) já voltou dentro do `desconto_reaberto` da mesa.
const jaNoReaberto = (p) => !!p.reaberto_em && p.status !== "concluido";

// Desconto gravado nos pedidos da sessão: cupom com nome e desconto manual (na
// 2ª via, o do fechamento) e o cashback usado pelo Mesa QR — que só fica no
// pedido (`cashback_usado`) e em `mesa.pagamentos.cashback_qr`.
function somarPedidos(pedidos) {
  const t = { manual: 0, cashback: 0, tipo: "", pct: 0, cupons: new Map() };
  for (const p of pedidos || []) {
    if (!p || p.status === "cancelado" || jaNoReaberto(p)) continue;
    const cod = String(p.cupom_codigo || "").trim().toUpperCase() || "cupom";
    t.cupons.set(cod, (t.cupons.get(cod) || 0) + centavos(p.cupom_desconto));
    t.manual += centavos(p.desconto_manual);
    t.cashback += centavos(p.cashback_usado);
    if (centavos(p.desconto_manual) > 0 && !t.tipo) {
      t.tipo = String(p.desconto_tipo || "").toLowerCase();
      t.pct = Number(p.desconto_percentual) || 0;
    }
  }
  return t;
}

function descontosDosPedidos(pedidos, pag, add) {
  const t = somarPedidos(pedidos);
  for (const [cod, c] of t.cupons) add({ valor: reais(c), tipo: "coupon", cupom: cod });
  add({ valor: reais(t.manual), tipo: t.tipo === "percent" ? "percent" : "fixed", percentual: t.pct });
  // O mesmo cashback está nos dois lugares: vale o maior, nunca a soma
  const qr = (Array.isArray(pag.cashback_qr) ? pag.cashback_qr : []).reduce((s, c) => s + centavos(c?.valor), 0);
  return Math.max(t.cashback, qr);
}

// Mesa (rodada da tela + acumulado das partes + o de antes da reabertura) e
// pedidos (cupom, manual, cashback) somam: o desconto da mesa só chega aos
// pedidos no fechamento final, depois de o cupom ser montado, e a reabertura
// zera o dos pedidos — nenhum conta duas vezes.
function descontosDaConta(mesa, pedidos, pag, opcoes) {
  const partes = [];
  const add = (m) => { if (m && centavos(m.valor) > 0) partes.push({ rotulo: rotuloDesconto(m), valor: centavos(m.valor) }); };
  const r = opcoes.descontoRodada;
  const informada = r !== undefined;
  add(typeof r === "number" ? { valor: r, tipo: "fixed" } : r);
  add(opcoes.descontoAcumulado !== undefined ? opcoes.descontoAcumulado : pag.desconto_acumulado);
  // O desconto de antes da reabertura já vem na rodada da tela de pagamento
  if (!informada) add(pag.desconto_reaberto);
  const cashback = descontosDosPedidos(pedidos, pag, add);
  if (cashback > 0) partes.push({ rotulo: "Cashback usado", valor: cashback });
  const soma = new Map();
  for (const p of partes) soma.set(p.rotulo, (soma.get(p.rotulo) || 0) + p.valor);
  return { descontos: [...soma].map(([rotulo, c]) => ({ rotulo, valor: reais(c) })), cashback: reais(cashback) };
}

const pagamentoDe = (p) => ({
  label: p.label || "",
  method: p.method || p.code || "",
  valor: reais(centavos(p.value ?? p.amount)),
  troco: p.troco_precisa ? Number(p.troco_valor) || 0 : 0,
  recebido: Number(p.cash_received) || 0,
});

/**
 * `opcoes`: `descontoRodada` (número ou {valor,tipo,percentual,cupom}: o que
 * está na tela de pagamento — fechamento final e "Imprimir conta" da tela;
 * informado, o `desconto_reaberto` não entra de novo), `descontoAcumulado`
 * (padrão: o gravado em `mesa.pagamentos`), `log` (pagamentos da sessão; padrão: `mesa.pagamentos.log`,
 * depois `logPadrao`), `agrupar` (padrão true; a 2ª via de Pedidos manda false).
 */
export function contaDoCupomMesa(mesa, pedidos, opcoes = {}) {
  const pag = lerPagamentos(mesa);
  const itens = juntarItens(mesa, pedidos);
  const { grupos, agrupado } = montarGrupos(itens, opcoes.agrupar !== false);
  let subtotalC = grupos.reduce((s, g) => s + centavos(g.subtotal), 0);
  if (subtotalC <= 0) subtotalC = centavos(mesa?.total);
  const { descontos, cashback } = descontosDaConta(mesa, pedidos, pag, opcoes);
  const descontoC = descontos.reduce((s, d) => s + centavos(d.valor), 0);
  const totalC = Math.max(0, subtotalC - descontoC);
  const doLog = [opcoes.log, pag.log, opcoes.logPadrao].find((l) => Array.isArray(l) && l.length > 0) || [];
  const pagamentos = doLog.map(pagamentoDe);
  const jaPagoC = pagamentos.reduce((s, p) => s + centavos(p.valor), 0);
  return {
    itens, grupos, agrupado, cashback, descontos, pagamentos,
    subtotal: reais(subtotalC), total: reais(totalC),
    ja_pago: reais(jaPagoC), falta_pagar: reais(Math.max(0, totalC - jaPagoC)),
  };
}

/** Versão para o servidor: os grupos apontam para `itens` pelo índice. */
export function contaParaServidor(conta) {
  return {
    agrupado: conta.agrupado,
    grupos: conta.grupos.map((g) => ({ chave: g.chave, rotulo: g.rotulo, subtotal: g.subtotal, idx: g.itens.map((i) => conta.itens.indexOf(i)) })),
    subtotal: conta.subtotal,
    descontos: conta.descontos,
    cashback: conta.cashback,
    total: conta.total,
    pagamentos: conta.pagamentos,
    ja_pago: conta.ja_pago,
    falta_pagar: conta.falta_pagar,
  };
}
